import { CommunicationInstanceAllStatus } from '@120wateraudit/communications-types'
import {
  Column,
  Filter,
  FilterType,
  OptionsList,
  OptionsListItem
} from '@120wateraudit/envirio-components'
import {
  PWSProgram,
  PWSSamplingEvent
} from '@120wateraudit/envirio-components/dist/models'
import moment from 'moment'
import React, { useState } from 'react'
import { Container, Popup, TableCell, TableRow } from 'semantic-ui-react'
import { AWS_BASE, getArchivedLetters } from 'src/API'
import { contactsClient } from 'src/apollo-clients'
import { ReactComponent as View } from 'src/assets/images/View.svg'
import CommunicationInstanceStatus from 'src/components/Communications/AuditLogGrid/CommunicationInstanceStatus'
import { TrackingLink } from 'src/components/Communications/AuditLogGrid/TrackingLink'
import { OverflowIcon } from 'src/components/Icons/Overflow'
import { GET_LOCATION_AUDIT_LOG_QUERY } from 'src/containers/Communications/data-access'
import { NoResults } from 'src/containers/Grid'
import { GQLGridWithHooks } from 'src/containers/Grid/GQLGridWithHooks'
import {
  auditLogFilters,
  sendDateFilter
} from 'src/containers/Grid/gridFilters/auditLogFilters'
import { useToggle } from 'src/hooks'
import { PDFArchiveViewer } from '../PDFPreview'

type SupportedColumn =
  | 'actions'
  | 'communicationType'
  | 'createdOn'
  | 'eventName'
  | 'name'
  | 'programName'
  | 'status'
  | 'trackingId'

interface AuditLogGridColumn {
  key: SupportedColumn
  name: string
  sortable: boolean
}

interface Props {
  accountId: number
  columns: AuditLogGridColumn[]
  locationId?: number
  parentRef?: ((grid: any) => void) | React.RefObject<any>
  programs?: PWSProgram[]
  samplingEvents?: PWSSamplingEvent[]
}

interface AuditLogResponse {
  communicationType: string
  createdOn: string
  documentId: number
  entityId: number
  entityType: string
  eventName: string
  id: string
  isCertifiedMail: boolean
  location: Location
  name: string
  programName: string
  status: CommunicationInstanceAllStatus
  trackingId: string
}

interface Location {
  address: string
  city: string
  postalCode: string
  stateOrProvince: string
}

const samplingEventLabelRenderer = (
  samplingEventOptions: any
): ((filter: Filter) => string) => {
  const getLabel = (filter: Filter) =>
    samplingEventOptions.filter(
      samplingEvent => samplingEvent.value === filter.value
    )[0].text

  return getLabel
}

const newGridDropdownFilter = (key: string, label: string, options: any) => {
  return {
    getValueLabel: samplingEventLabelRenderer(options),
    key,
    label,
    options,
    type: FilterType.ListSelection,
    value: key
  }
}

function RenderColumns({
  accountId,
  auditLog,
  columns,
  viewCommunicationArchive
}: {
  accountId: number
  auditLog: AuditLogResponse
  columns: Column[]
  viewCommunicationArchive: (communicationInstanceId: number) => void
}) {
  const {
    createdOn,
    entityId,
    entityType,
    eventName,
    id,
    name,
    programName,
    status,
    trackingId
  } = auditLog
  const [showContextMenu, toggleContextMenu] = useToggle()

  const columnDefinitions: { [key in SupportedColumn]: JSX.Element } = {
    actions: (
      <Popup
        basic
        on="click"
        onClose={toggleContextMenu}
        open={showContextMenu}
        position="bottom right"
        trigger={
          <OverflowIcon
            onClick={() => {
              if (
                entityType === 'CommunicationInstance' ||
                (entityType === 'CommunicationBatchLetter' &&
                  status === 'Processed')
              ) {
                toggleContextMenu()
              }
            }}
          />
        }>
        <OptionsList>
          <OptionsListItem
            onClick={e => {
              if (e?.target) {
                toggleContextMenu()

                viewCommunicationArchive(entityId)
              }
            }}>
            <View
              style={{
                opacity: 0.25,
                width: '24px',
                height: '24px',
                marginLeft: '8px',
                marginRight: '8px'
              }}
            />
            <span>View Letter</span>
          </OptionsListItem>
        </OptionsList>
      </Popup>
    ),
    communicationType: (
      <>
        {entityType === 'CommunicationInstance'
          ? 'Sent Letters'
          : 'Downloaded Letters'}
      </>
    ),
    createdOn: <>{moment(createdOn).format('MM/DD/YYYY')}</>,
    eventName: <>{eventName}</>,
    name: <>{name}</>,
    programName: <>{programName}</>,
    status: (
      <CommunicationInstanceStatus accountId={accountId} status={status} />
    ),
    trackingId: (
      <TrackingLink
        createdOn={createdOn}
        status={status}
        trackingId={trackingId}
      />
    )
  }

  return columns.map(column => {
    return (
      <TableCell key={`AuditLogCol${id}${column.key}`}>
        {columnDefinitions[column.key]}
      </TableCell>
    )
  })
}

const RenderRowWithData: React.FC<{
  accountId: number
  auditLog: AuditLogResponse
  columns: Column[]
}> = ({ accountId, auditLog, columns }) => {
  const { location, name } = auditLog
  const [isArchiveModalOpen, toggleArchiveModal] = useToggle(false)
  const [archiveUrl, setArchiveUrl] = useState<undefined | string>(undefined)

  const viewCommunicationArchive = async (communicationInstanceId: number) => {
    const archivedLetters = await getArchivedLetters({
      accountId,
      communicationInstanceId
    })

    if (archivedLetters.length > 0) {
      setArchiveUrl(archivedLetters[0].url)
      toggleArchiveModal()
    }
  }

  return (
    <TableRow>
      {RenderColumns({
        accountId,
        auditLog,
        columns,
        viewCommunicationArchive
      })}
      {isArchiveModalOpen && archiveUrl && (
        <PDFArchiveViewer
          isOpen={isArchiveModalOpen}
          title={`${location.address}-${name}-${new Date().toISOString()}`}
          toggle={toggleArchiveModal}
          url={archiveUrl}
        />
      )}
    </TableRow>
  )
}

const AuditLogGrid = ({
  accountId,
  columns,
  locationId,
  parentRef,
  programs,
  samplingEvents
}: Props) => {
  const programsOptionsList: any[] = []
  const samplingEventsOptionsList: any[] = []
  const queryName = 'locationAuditLogs'

  let programNameFilter = {}
  let eventFilter = {}

  if (samplingEvents && samplingEvents.length) {
    samplingEvents.forEach((samplingEvent: PWSSamplingEvent) => {
      const samplingEventOption = {
        text: samplingEvent.name,
        value: samplingEvent.id
      }
      samplingEventsOptionsList.push(samplingEventOption)
    })

    eventFilter = newGridDropdownFilter(
      'eventId',
      'Sampling Events',
      samplingEventsOptionsList
    )
    Object.assign(auditLogFilters, { eventName: eventFilter })
  }

  if (programs && programs.length) {
    programs.forEach((program: PWSProgram) => {
      const programOption = { text: program.name, value: program.id }
      programsOptionsList.push(programOption)
    })

    programNameFilter = newGridDropdownFilter(
      'programId',
      'Programs',
      programsOptionsList
    )
    Object.assign(auditLogFilters, { programName: programNameFilter })
  }

  return (
    <Container>
      <GQLGridWithHooks
        availableFilters={auditLogFilters}
        client={contactsClient}
        columns={columns}
        entityName="communication"
        exportURL={`${AWS_BASE}/platform/communications/rest/accounts/${accountId}/letterLog/export`}
        getGridFilters={() => true}
        getSearchValue={() => true}
        noResultsFound={
          <NoResults
            pluralEntityName="Communications"
            entityName="Communication"
          />
        }
        parentRef={parentRef}
        pluralEntityName="communications"
        pollInterval={5000}
        query={GET_LOCATION_AUDIT_LOG_QUERY}
        queryName={queryName}
        renderRow={item => (
          <RenderRowWithData
            key={`AuditLogRow-${item.id}`}
            columns={columns}
            accountId={accountId}
            auditLog={item}
          />
        )}
        topRightFilter={sendDateFilter}
        totalGetter={data => data.auditLogs.total}
        variables={{ accountId, locationId }}
      />
    </Container>
  )
}

export default AuditLogGrid
